import { ReactNode } from "react";
import { motion } from "framer-motion";
import { LucideIcon } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { CalculatorThemeProvider, useCalculatorTheme } from "@/contexts/CalculatorThemeContext";
import CalculatorThemeToggle from "./CalculatorThemeToggle";

interface CalculatorLayoutProps {
  children: ReactNode;
  title: string;
  subtitle: string;
  icon: LucideIcon;
  badge?: string;
}

// Inner component that uses the theme context
const CalculatorLayoutInner = ({ 
  children, 
  title, 
  subtitle, 
  icon: Icon,
  badge 
}: CalculatorLayoutProps) => {
  const { isDark } = useCalculatorTheme();

  // Theme-aware color values
  const colors = {
    heroGradient: isDark 
      ? "linear-gradient(to bottom, hsl(222 47% 8%), hsl(222 35% 18%), hsl(222 47% 10%))"
      : "hsl(var(--brand-surface))",
    mainGradient: isDark 
      ? "linear-gradient(to bottom, hsl(222 47% 10%), hsl(222 47% 8%))"
      : "hsl(var(--brand-paper))",
    gridColor: isDark ? "hsl(45 93% 58%)" : "hsl(222 47% 15%)",
    orbGradient: isDark 
      ? "radial-gradient(circle, hsla(45, 93%, 58%, 0.1) 0%, transparent 70%)"
      : "transparent",
    iconBg: isDark 
      ? "linear-gradient(135deg, hsla(45, 93%, 58%, 0.2), hsla(38, 90%, 50%, 0.1))"
      : "linear-gradient(135deg, hsla(222, 47%, 15%, 0.1), hsla(222, 35%, 25%, 0.05))",
    iconBorder: isDark ? "hsla(45, 93%, 58%, 0.3)" : "hsla(222, 47%, 15%, 0.2)",
    iconShadow: isDark ? "0 0 30px hsla(45, 93%, 58%, 0.2)" : "0 0 30px hsla(222, 47%, 15%, 0.1)",
    iconColor: isDark ? "hsl(45 93% 58%)" : "hsl(222 47% 15%)",
    badgeBg: isDark ? "hsla(45, 93%, 58%, 0.1)" : "hsla(222, 47%, 15%, 0.05)",
    badgeBorder: isDark ? "hsla(45, 93%, 58%, 0.3)" : "hsla(222, 47%, 15%, 0.2)",
    badgeText: isDark ? "hsl(45 80% 70%)" : "hsl(222 47% 25%)",
    titleColor: isDark ? "#ffffff" : "hsl(222 47% 15%)",
    subtitleColor: isDark ? "hsla(0, 0%, 100%, 0.7)" : "hsla(222, 47%, 15%, 0.75)",

    bottomFade: isDark 
      ? "linear-gradient(to bottom, transparent, hsl(222 47% 10%))"
      : "linear-gradient(to bottom, transparent, hsl(var(--brand-paper)))",
  };

  return (
    <div className={`min-h-screen flex flex-col ${isDark ? "font-[Manrope,sans-serif]" : "brand-site"}`}>
      <Header />
      
      {/* Hero Section with theme-aware background */}
      <div className="relative pt-24 md:pt-32 pb-10 md:pb-12 overflow-hidden">
        {/* Background gradient */}
        <div 
          className="absolute inset-0 transition-colors duration-500"
          style={{ background: colors.heroGradient }}
        />
        
        {/* Grid overlay */}
        <div 
          className="absolute inset-0 opacity-[0.03] transition-opacity duration-500"
          style={{
            backgroundImage: `
              linear-gradient(to right, ${colors.gridColor} 1px, transparent 1px),
              linear-gradient(to bottom, ${colors.gridColor} 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />
        
        {/* Floating orb */}
        <motion.div
          className="absolute w-[400px] h-[400px] rounded-full transition-all duration-500"
          style={{
            background: colors.orbGradient,
            filter: "blur(60px)",
            top: "-20%",
            right: "-10%",
          }}
          animate={{
            x: [0, 30, -20, 0],
            y: [0, 20, -10, 0],
            scale: [1, 1.1, 0.95, 1],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        {/* Content */}
        <div className="container relative z-10">
          {/* Theme Toggle */}
          <div className="flex justify-end mb-2 md:mb-0 md:absolute md:top-0 md:right-8">
            <CalculatorThemeToggle />
          </div>


          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div 
                className="p-4 rounded-2xl transition-all duration-500"
                style={{
                  background: colors.iconBg,
                  border: `1px solid ${colors.iconBorder}`,
                  boxShadow: colors.iconShadow,
                }}
              >
                <Icon 
                  className="h-10 w-10 transition-colors duration-500" 
                  style={{ color: colors.iconColor }}
                />
              </div>
            </div>
            
            {/* Badge */}
            {badge && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 }}
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4 transition-all duration-500"
                style={{
                  background: colors.badgeBg,
                  border: `1px solid ${colors.badgeBorder}`,
                }}
              >
                <span 
                  className="text-sm font-medium transition-colors duration-500"
                  style={{ color: colors.badgeText }}
                >
                  {badge}
                </span>
              </motion.div>
            )}
            
            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-normal mb-4 transition-all duration-500"
              style={{ color: colors.titleColor }}

            >
              {title}
            </motion.h1>
            
            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg max-w-2xl mx-auto transition-colors duration-500"
              style={{ color: colors.subtitleColor }}
            >
              {subtitle}
            </motion.p>
          </motion.div>
        </div>
        
        {/* Bottom fade */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-24 transition-all duration-500"
          style={{ background: colors.bottomFade }}
        />
      </div>
      
      {/* Main content area with theme-aware background */}
      <main 
        className="flex-1 relative transition-all duration-500"
        style={{ background: colors.mainGradient }}
      >
        {/* Subtle grid overlay */}
        <div 
          className="absolute inset-0 opacity-[0.02] transition-opacity duration-500"
          style={{
            backgroundImage: `
              linear-gradient(to right, ${colors.gridColor} 1px, transparent 1px),
              linear-gradient(to bottom, ${colors.gridColor} 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />
        
        <div className="container relative z-10 pb-16">
          {children}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

// Wrapper component that provides the theme context
const CalculatorLayout = (props: CalculatorLayoutProps) => {
  return (
    <CalculatorThemeProvider>
      <CalculatorLayoutInner {...props} />
    </CalculatorThemeProvider>
  );
};

export default CalculatorLayout;
